import useNotifier from "@saleor/hooks/useNotifier";
import { useCallback, useReducer } from "react";
import { useMutation } from "react-apollo";
import { useIntl } from "react-intl";

import {
  VOUCHER_CONDITION_CREATE,
  VOUCHER_CONDITION_DELETE,
  VOUCHER_CONDITION_UPDATE
} from "../mutations";
import { VoucherDetails } from "../types/VoucherDetails";

// Types, Interfaces, and Constants -------------------------------------------

export const OPERATION_MIN_VALUES = {
  EQ: 0,
  GE: 0,
  GT: 0,
  LE: 1,
  LT: 1
};

export type TOperation = "EQ" | "LT" | "LE" | "GT" | "GE";

export interface IVoucherCondition {
  operation: TOperation;
  value: number;
}

export interface IVoucherLimitState {
  hasBeenModified: boolean;
  isLimitToCustomerActivity: boolean;
  isWithOngoingOrders: boolean;
  ongoingOrderVoucherCondition: IVoucherCondition;
  isWithSwipeCoupons: boolean;
  swipeCouponVoucherCondition: IVoucherCondition;
}

export type IVoucherLimitActions =
  | {
      type: "re-initialize";
      voucherLimitState?: Partial<IVoucherLimitState>;
    }
  | {
      type: "toggle-checkbox";
      voucherConditionType: "ongoing-order" | "swipe-coupons";
    }
  | {
      type: "toggle-limit-customer-activity";
      value: boolean;
    }
  | {
      type: "update-value";
      voucherConditionType: "ongoing-order" | "swipe-coupons";
      value: number;
    }
  | {
      type: "update-condition";
      voucherConditionType: "ongoing-order" | "swipe-coupons";
      value: TOperation;
    };

// Utility functions ----------------------------------------------------------

export const initializeVoucherCondition = (): IVoucherCondition => ({
  operation: "EQ",
  value: 0
});

// This function reshapes the voucherLimitState found on reducer to API request body shape.
const voucherConditionInputShaper = (
  voucherLimitReducerState: IVoucherLimitState
) => {
  const voucherConditionsInput = [];
  if (voucherLimitReducerState.isWithOngoingOrders) {
    voucherConditionsInput.push({
      metric: "COMPLETED_ORDER",
      operation:
        voucherLimitReducerState.ongoingOrderVoucherCondition.operation,
      value: voucherLimitReducerState.ongoingOrderVoucherCondition.value
    });
  }
  if (voucherLimitReducerState.isWithSwipeCoupons) {
    voucherConditionsInput.push({
      metric: "REDEEMED_COUPON",
      operation: voucherLimitReducerState.swipeCouponVoucherCondition.operation,
      value: voucherLimitReducerState.swipeCouponVoucherCondition.value
    });
  }
  return voucherConditionsInput;
};

const initializeVoucherLimitState = (): IVoucherLimitState => ({
  hasBeenModified: false,
  isLimitToCustomerActivity: false,
  isWithOngoingOrders: false,
  isWithSwipeCoupons: false,
  ongoingOrderVoucherCondition: initializeVoucherCondition(),
  swipeCouponVoucherCondition: initializeVoucherCondition()
});

const voucherLimitReducer = (
  state: IVoucherLimitState,
  action: IVoucherLimitActions
): IVoucherLimitState => {
  let fieldKeyToBeUpdated = "some-place-holder-value";
  switch (action.type) {
    case "re-initialize":
      if (action.voucherLimitState === undefined) {
        return initializeVoucherLimitState();
      }
      return {
        ...initializeVoucherLimitState(),
        ...action.voucherLimitState
      };
    case "update-value":
      fieldKeyToBeUpdated =
        action.voucherConditionType === "ongoing-order"
          ? "ongoingOrderVoucherCondition"
          : "swipeCouponVoucherCondition";

      // For following Minimum value rule depending on the operation selected.
      const newValue =
        OPERATION_MIN_VALUES[state[fieldKeyToBeUpdated].operation] >
        action.value
          ? OPERATION_MIN_VALUES[state[fieldKeyToBeUpdated].operation]
          : action.value;

      return {
        ...state,
        [fieldKeyToBeUpdated]: {
          ...state[fieldKeyToBeUpdated],
          value: newValue
        },
        hasBeenModified: true
      };
    case "update-condition":
      fieldKeyToBeUpdated =
        action.voucherConditionType === "ongoing-order"
          ? "ongoingOrderVoucherCondition"
          : "swipeCouponVoucherCondition";

      return {
        ...state,
        [fieldKeyToBeUpdated]: {
          operation: action.value,
          value:
            state[fieldKeyToBeUpdated].value <
            OPERATION_MIN_VALUES[action.value]
              ? OPERATION_MIN_VALUES[action.value]
              : state[fieldKeyToBeUpdated].value
        },
        hasBeenModified: true
      };
    case "toggle-checkbox": {
      if (action.voucherConditionType === "ongoing-order") {
        return {
          ...state,
          hasBeenModified: true,
          isWithOngoingOrders: !state.isWithOngoingOrders,
          ongoingOrderVoucherCondition: initializeVoucherCondition()
        };
      } else {
        return {
          ...state,
          hasBeenModified: true,
          isWithSwipeCoupons: !state.isWithSwipeCoupons,
          swipeCouponVoucherCondition: initializeVoucherCondition()
        };
      }
    }
    case "toggle-limit-customer-activity": {
      return {
        ...state,
        hasBeenModified: true,
        isLimitToCustomerActivity: action.value
      };
    }
  }
};

// useVoucherLimitsData hook --------------------------------------------------

const useVoucherLimitsData = () => {
  const notify = useNotifier();
  const intl = useIntl();

  const [voucherConditionCreateMutation] = useMutation(
    VOUCHER_CONDITION_CREATE
  );

  const [voucherConditionUpdateMutation] = useMutation(
    VOUCHER_CONDITION_UPDATE
  );

  const [voucherConditionDeleteMutation] = useMutation(
    VOUCHER_CONDITION_DELETE
  );

  const [voucherLimitState, voucherLimitDispatcher] = useReducer(
    voucherLimitReducer,
    null,
    initializeVoucherLimitState
  );

  const updateFailedCallback = useCallback(() => {
    notify({
      status: "error",
      text: intl.formatMessage({
        defaultMessage: "Failed to modify voucher condition(s)."
      })
    });
  }, [notify, intl]);

  const voucherConditionCreateApi = useCallback(
    (voucherId: string) =>
      voucherConditionCreateMutation({
        variables: {
          voucherConditionsInput: voucherConditionInputShaper(
            voucherLimitState
          ),
          voucherId
        }
      }),
    [voucherConditionCreateMutation, voucherLimitState]
  );

  const voucherConditionUpdate = (voucherDetails: VoucherDetails) => {
    // Handle Ongoing orders field:
    const ongoingOrderDetails = voucherDetails.voucher.voucherConditions.edges.find(
      val => val.node.metric === "COMPLETED_ORDER"
    );
    if (ongoingOrderDetails !== undefined) {
      if (voucherLimitState.isWithOngoingOrders) {
        if (
          voucherLimitState.ongoingOrderVoucherCondition.operation !==
            ongoingOrderDetails.node.operation ||
          voucherLimitState.ongoingOrderVoucherCondition.value !==
            ongoingOrderDetails.node.value
        ) {
          // Case where the existing voucherCondition is modified by the user
          voucherConditionUpdateMutation({
            variables: {
              voucherConditionId: ongoingOrderDetails.node.id,
              voucherConditionsInput: {
                metric: "COMPLETED_ORDER",
                operation:
                  voucherLimitState.ongoingOrderVoucherCondition.operation,
                value: voucherLimitState.ongoingOrderVoucherCondition.value
              }
            }
          }).catch(updateFailedCallback);
        }
      } else {
        // Case where the existing voucherCondition will be unchecked by the user
        voucherConditionDeleteMutation({
          variables: { voucherConditionId: ongoingOrderDetails.node.id }
        }).catch(updateFailedCallback);
      }
    } else if (voucherLimitState.isWithOngoingOrders) {
      // Case where the voucherCondition will be checked by the user
      voucherConditionCreateMutation({
        variables: {
          voucherConditionsInput: [
            {
              metric: "COMPLETED_ORDER",
              operation:
                voucherLimitState.ongoingOrderVoucherCondition.operation,
              value: voucherLimitState.ongoingOrderVoucherCondition.value
            }
          ],
          voucherId: voucherDetails.voucher.id
        }
      }).catch(updateFailedCallback);
    }

    // Handle Swipe coupons field:
    const swipeCouponsDetails = voucherDetails.voucher.voucherConditions.edges.find(
      val => val.node.metric === "REDEEMED_COUPON"
    );
    if (swipeCouponsDetails !== undefined) {
      if (voucherLimitState.isWithSwipeCoupons) {
        if (
          voucherLimitState.swipeCouponVoucherCondition.operation !==
            swipeCouponsDetails.node.operation ||
          voucherLimitState.swipeCouponVoucherCondition.value !==
            swipeCouponsDetails.node.value
        ) {
          // Case where the existing voucherCondition is modified by the user
          voucherConditionUpdateMutation({
            variables: {
              voucherConditionId: swipeCouponsDetails.node.id,
              voucherConditionsInput: {
                metric: "REDEEMED_COUPON",
                operation:
                  voucherLimitState.swipeCouponVoucherCondition.operation,
                value: voucherLimitState.swipeCouponVoucherCondition.value
              }
            }
          }).catch(updateFailedCallback);
        }
      } else {
        // Case where the existing voucherCondition will be unchecked by the user
        voucherConditionDeleteMutation({
          variables: { voucherConditionId: swipeCouponsDetails.node.id }
        }).catch(updateFailedCallback);
      }
    } else if (voucherLimitState.isWithSwipeCoupons) {
      // Case where the voucherCondition will be checked by the user
      voucherConditionCreateMutation({
        variables: {
          voucherConditionsInput: [
            {
              metric: "REDEEMED_COUPON",
              operation:
                voucherLimitState.swipeCouponVoucherCondition.operation,
              value: voucherLimitState.swipeCouponVoucherCondition.value
            }
          ],
          voucherId: voucherDetails.voucher.id
        }
      }).catch(updateFailedCallback);
    }
  };

  return {
    voucherConditionCreateApi,
    voucherConditionUpdate,
    voucherLimitDispatcher,
    voucherLimitState
  };
};

export default useVoucherLimitsData;
